type ConfigType = {
    APP_BASE_URL: string;
    APP_PORT: string;
    API_WS_PROTOCOL: string;
    API_REST_PROTOCOL: string;
    API_BASE_DOMAIN: string;
    API_AUTH_PORT: string;
    API_BASE_URL: string;
    WS_BASE_URL: string;
    GOOGLE_OAUTH_CALLBACK_PATH: string;
    GOOGLE_OAUTH_CALLBACK_FULL_URL: string;
    CONTACT_EMAIL: string;
    /**
     * Roles allowed to access the dashboard. Empty set = landing-only mode
     * (no logins permitted). Used as the post-auth gate; non-allowed roles
     * are logged out + bounced.
     */
    ALLOWED_ROLES: ReadonlySet<string>;
    /**
     * Whether the public-facing user app is open (i.e., 'candidate' or
     * 'employer' is in ALLOWED_ROLES). Drives visibility of Logga in / Skapa
     * konto CTAs on the landing page. Admin-only soft launches keep this
     * false; the /admin route is always reachable regardless.
     */
    PUBLIC_APP_ENABLED: boolean;
    /**
     * Deployment environment (#76): 'dev' | 'qa' | 'prod'. Mirrors the backend
     * APP_ENV. Set VITE_APP_ENV; unset falls back to Vite's build flag
     * (dev build → 'dev', production build → 'prod'). Drives SHOW_DEV_TOOLS and
     * the non-prod environment ribbon.
     */
    APP_ENV: 'dev' | 'qa' | 'prod';
    /**
     * Master switch for DEV-only affordances that shouldn't reach end users:
     * the Link LLM model-picker + tool-call ("Verktygsanrop") toggle and the
     * raw tool-activity rows they reveal, and test-only menu actions (e.g.
     * "Förfalla nu (test)", "🧪 Fyll testdata"). Derived from APP_ENV (on only
     * in 'dev'); set VITE_SHOW_DEV_TOOLS=true/false to force it. The Link
     * *default model* in production is set on the BACKEND
     * (LINK_AGENT_PROVIDER / LINK_AGENT_MODEL), not here.
     */
    SHOW_DEV_TOOLS: boolean;
};

// Parse an explicit "true"/"false" env override, falling back when unset/blank.
const parseBool = (raw: string | undefined, fallback: boolean): boolean =>
    raw === undefined || raw === '' ? fallback : raw === 'true';

const parseAllowedRoles = (raw: string | undefined): Set<string> =>
    new Set((raw ?? '').split(',').map((r) => r.trim()).filter(Boolean));

// Normalize VITE_APP_ENV to dev|qa|prod; unknown/unset → derive from the Vite
// build flag (dev build → dev, prod build → prod). Anything unrecognized is
// treated as prod so a typo can't unlock dev tooling in a live build.
const parseAppEnv = (raw: string | undefined): 'dev' | 'qa' | 'prod' => {
    const v = (raw ?? '').trim().toLowerCase();
    if (v === 'dev' || v === 'qa' || v === 'prod') return v;
    return import.meta.env.DEV ? 'dev' : 'prod';
};
const APP_ENV = parseAppEnv(import.meta.env.VITE_APP_ENV);

// Check if running locally based on an explicit environment variable
const IS_LOCAL = import.meta.env.VITE_ENVIRONMENT === "local";

const CONFIG: ConfigType = {
    // ### APP ###
    // Note: APP_BASE_URL should NOT include protocol (e.g., "localhost" not "http://localhost")
    APP_BASE_URL: import.meta.env.VITE_APP_BASE_URL || 'localhost',
    APP_PORT: import.meta.env.VITE_APP_PORT !== undefined ? import.meta.env.VITE_APP_PORT : ':3000',

    // ### API BASE PARTS ###
    API_WS_PROTOCOL: import.meta.env.VITE_API_WS_PROTOCOL || (IS_LOCAL ? 'ws://' : 'wss://'),
    API_REST_PROTOCOL: import.meta.env.VITE_API_REST_PROTOCOL || (IS_LOCAL ? 'http://' : 'https://'),
    API_BASE_DOMAIN: import.meta.env.VITE_API_BASE_DOMAIN || (IS_LOCAL ? 'localhost' : 'api.nexilink.se'),

    // API Endpoints - Use ports locally, paths in Kubernetes
    API_AUTH_PORT: IS_LOCAL ? (import.meta.env.VITE_API_AUTH_PORT || ':3500') : '',

    // ### CONTACT ###
    CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL,

    // ### ROLE ALLOWLIST ###
    ALLOWED_ROLES: parseAllowedRoles(import.meta.env.VITE_ALLOWED_ROLES),
    PUBLIC_APP_ENABLED: false, // computed below — depends on ALLOWED_ROLES

    // ### ENVIRONMENT ###
    APP_ENV,

    // ### DEV AFFORDANCES ###
    // Explicit VITE_SHOW_DEV_TOOLS wins; unset derives from APP_ENV (dev tooling
    // only in the 'dev' environment — hidden in qa + prod).
    SHOW_DEV_TOOLS: parseBool(import.meta.env.VITE_SHOW_DEV_TOOLS, APP_ENV === 'dev'),

    // ### GOOGLE OAUTH ###
    GOOGLE_OAUTH_CALLBACK_PATH: import.meta.env.VITE_GOOGLE_OAUTH_CALLBACK_PATH || '/auth/google',

    // Construct API URLs dynamically
    API_BASE_URL: '',
    WS_BASE_URL: '',
    GOOGLE_OAUTH_CALLBACK_FULL_URL: '',
};

// Construct full API URLs
CONFIG.API_BASE_URL = IS_LOCAL
    ? `${CONFIG.API_REST_PROTOCOL}${CONFIG.API_BASE_DOMAIN}`
    : `${CONFIG.API_REST_PROTOCOL}${CONFIG.API_BASE_DOMAIN}`;

CONFIG.WS_BASE_URL = IS_LOCAL
    ? `${CONFIG.API_WS_PROTOCOL}${CONFIG.API_BASE_DOMAIN}`
    : `${CONFIG.API_WS_PROTOCOL}${CONFIG.API_BASE_DOMAIN}`;

// Construct full Google OAuth callback URL
// Always prepend the REST protocol to APP_BASE_URL
CONFIG.GOOGLE_OAUTH_CALLBACK_FULL_URL = `${CONFIG.API_REST_PROTOCOL}${CONFIG.APP_BASE_URL}${CONFIG.APP_PORT}${CONFIG.GOOGLE_OAUTH_CALLBACK_PATH}`;

// Compute PUBLIC_APP_ENABLED — landing CTAs are visible only when end users
// can actually log in (admin-only mode hides them).
CONFIG.PUBLIC_APP_ENABLED = CONFIG.ALLOWED_ROLES.has('candidate') || CONFIG.ALLOWED_ROLES.has('employer');

// Log the final configuration — dev only, never dump API URLs/roles in prod (#76).
if (import.meta.env.DEV) {
    console.log('CONFIG:', CONFIG);
}

export default CONFIG;